<template>
  <div class="p-the-header-navi-for-sp-layout">
    <button
      @click.stop
      @click="switchShownState()"
      class="p-the-header-navi-for-sp-layout__button"
    >
      <IconBase
        :width="24"
        :height="24"
        view-box="0 0 24 24"
        icon-color="#000000"
      >
        <IconHamburger />
      </IconBase>
    </button>

    <div
      class="p-the-header-navi-for-sp-layout__drawer"
      ref="drawerContainer"
    >
      <div
        @click.stop
        class="p-the-header-navi-for-sp-layout__drawer__inner"
        ref="drawer"
      >
        <TheHeaderNaviProfilePanel />

        <HeaderGroupNavi v-if="isAdminSite && isSp" />

        <HeaderNaviSwitchButtonForSpLayout
          v-if="isUserAdmin"
          @click-change-modal-state="emitClickEvent()"
          >{{ switchButton.text }}</HeaderNaviSwitchButtonForSpLayout
        >

        <component :is="currentComponent" v-bind="currentProperties" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { gsap } from "gsap";
import TheHeaderNaviOnGeneral from "@/common/components/organisms/header/general/TheHeaderNaviOnGeneral.vue";
import TheHeaderNaviOnAdmin from "@/common/components/organisms/header/admin/TheHeaderNaviOnAdmin.vue";
import HeaderGroupNavi from "@/common/components/organisms/header/admin/HeaderGroupNavi.vue";

import IconBase from "@/common/components/atoms/IconBase.vue";
import IconHamburger from "@/common/components/icons/IconHamburger.vue";
import TheHeaderNaviProfilePanel from "@/common/components/organisms/header/TheHeaderNaviProfilePanel.vue";
import HeaderNaviSwitchButtonForSpLayout from "@/common/components/organisms/header/HeaderNaviSwitchButtonForSpLayout.vue";

import { Options, Vue } from 'vue-class-component';
import commons from "@/common/store/modules/commons";
import modalState from "@/common/store/modules/modalState";

import globals from "@/general/store/modules/globals";
import adminGlobals from "@/admin/store/modules/globals";
import LocalStorage from "@/general/utils/storage";

@Options({
  name: "TheHeaderNaviForSpLayout",
  props: {
	whatsNewUnreadCount: { type: Number, required: false, default: 0 },
	thanksPointUnreadCount: { type: Number, required: false, default: 0 },
	point: { type: Number, required: true },
	isUserAdmin: { type: String, required: true, default: "0" }
  },
  components: {
    TheHeaderNaviOnGeneral,
    TheHeaderNaviOnAdmin,
    HeaderGroupNavi,
    IconBase,
    IconHamburger,
    TheHeaderNaviProfilePanel,
    HeaderNaviSwitchButtonForSpLayout
  },
  watch: {
      shown(newVal: boolean) {
        modalState.switchHtmlElementScrollFixedStyle(newVal);
      },
      closeModalState() {
        if (!this.shown) return;
        this.shown = false;
        this.animationShownDrawer(this.shown);
      }
    }

})
export default class TheHeaderNaviForSpLayout extends Vue {
  whatsNewUnreadCount!: number;
  thanksPointUnreadCount!: number;
  point!: number;
  isUserAdmin!: string;

  shown = false;
  switchButton: Record<string, any> = {};

  get currentComponent() {
    const pageType = this.headerInfo.pageType;
    const componentInitials = pageType.charAt(0).toUpperCase();
    const componentSuffix = `${componentInitials}${pageType.slice(1)}`;
    return `TheHeaderNaviOn${componentSuffix}`;
  }

  get currentProperties() {
    return {
      whatsNewUnreadCount: this.whatsNewUnreadCount,
      thanksPointUnreadCount: this.thanksPointUnreadCount,
      point: this.point
    };
  }

  get headerInfo() {
    return commons.headerInfo;
  }

  get closeModalState() {
    return modalState.closeModalState;
  }

  get isSp() {
    if (this.isAdminSite) {
      return adminGlobals.isLargeBreakpointWidthOfSp;
    }
    return globals.isLargeBreakpointWidthOfSp;
  }

  get isAdminSite() {
    return false;
  }

  mounted() {
    modalState.switchHtmlElementScrollFixedStyle(this.shown);
    this.initDrawerStyle();
    this.initSwitchButtonObject();
  }

  switchShownState() {
    // if (shown === false) this.shown = true;
    this.shown = !this.shown;
    this.animationShownDrawer(this.shown);
  }

  initDrawerStyle() {
    const refs = this.$refs;
    const drawer = refs.drawer as HTMLElement;
	const drawerContainer = refs.drawerContainer as HTMLElement;

    gsap.set(drawer, {
      x: "100%"
    });

    gsap.set(drawerContainer, {
      autoAlpha: 0
    });
  }

  animationShownDrawer(shown: boolean) {
    const refs = this.$refs;
    const drawerContainer = refs.drawerContainer as HTMLElement;
    const drawer = refs.drawer as HTMLElement;
    const openTl = gsap.timeline();
    const closeTl = gsap.timeline();
    const speed = 0.2;
    const delay = 0.08;

    const animationObj = {
      true() {
        openTl
          .to(drawerContainer, speed / 2, {
            delay: delay,
            autoAlpha: 1
          })
          .to(drawer, speed, {
            x: "0%"
          });
      },

      false() {
        closeTl
          .to(drawer, speed, {
            x: "100%"
          })
          .to(drawerContainer, speed / 2, {
            autoAlpha: 0
          });
      }
    };

    animationObj[shown === true ? "true" : "false"]();
  }

  initSwitchButtonObject() {
    let pageType = "general";
    if (this.isAdminSite) {
      pageType = "admin";
    } else {
      pageType = "general";
    }
    this.switchButton = this.headerInfo.switch[pageType];
  }

  emitClickEvent() {
    this.$emit("click-change-modal-state");
    modalState.setCloseModalState(!modalState.closeModalState);
  }
}
</script>

<style lang="scss" scoped>
.p-the-header-navi-for-sp-layout {
  &__button {
    width: rem(48);
    height: rem(48);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__drawer {
    position: fixed;
    left: 0;
    top: 0;
    z-index: 999;
    height: 100vh;
    width: 100vw;
    background-color: rgba($common-black, 0.75);

    &__inner {
      width: rem(256);
      margin-left: auto;
      overflow-y: auto;
      overflow-x: hidden;
      height: 100vh;
      background-color: $common-white;
      padding-bottom: rem(250);
    }

    &__body {
      margin-top: rem(10);
    }
  }
}
</style>
